import type { ReactNode } from "react";
import type { DashboardScope } from "~/components/dashboard/DashboardScopeChip";

import { Box, Icon, Paper, Skeleton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";

import DashboardScopeChip from "~/components/dashboard/DashboardScopeChip";
import RetryableErrorState from "~/components/RetryableErrorState";
import { CaptionText } from "~/components/Typographies";

interface DashboardKpiTileProps {
	errorMessage?: string;
	footer?: ReactNode;
	hint?: string;
	icon?: string;
	isError?: boolean;
	isLoading?: boolean;
	label: string;
	onClick?: () => void;
	onRetry?: () => void;
	retryLabel?: string;
	scope?: DashboardScope;
	value: string;
}

const DashboardKpiTile = ({
	errorMessage,
	footer,
	hint,
	icon,
	isError,
	isLoading,
	label,
	onClick,
	onRetry,
	retryLabel,
	scope,
	value
}: DashboardKpiTileProps) => (
	<Paper
		elevation={0}
		onClick={onClick}
		sx={{
			"&::before": {
				backgroundColor: "primary.main",
				borderRadius: "3px 0 0 3px",
				bottom: 0,
				content: '""',
				left: 0,
				opacity: 0,
				position: "absolute",
				top: 0,
				transition: "opacity 0.2s ease",
				width: 4
			},
			backgroundColor: "background.paper",
			border: "1px solid",
			borderColor: "divider",
			borderRadius: 3,
			boxShadow: (theme) => `0 1px 3px ${alpha(theme.palette.common.black, 0.06)}`,
			display: "flex",
			gap: 1.5,
			overflow: "hidden",
			p: 2.5,
			position: "relative",
			transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
			...(onClick && {
				"&:hover": {
					"&::before": { opacity: 1 },
					boxShadow: (theme) => `0 10px 24px ${alpha(theme.palette.common.black, 0.1)}`,
					transform: "translateY(-2px)"
				},
				cursor: "pointer"
			})
		}}
	>
		<Box sx={{ flex: 1, minWidth: 0 }}>
			<Box sx={{ alignItems: "center", display: "flex", gap: 1, justifyContent: "space-between" }}>
				<CaptionText
					sx={{
						color: "text.secondary",
						fontFamily: "'Montserrat', sans-serif",
						fontSize: "0.68rem",
						fontWeight: 600,
						letterSpacing: "0.08em",
						textTransform: "uppercase",
						whiteSpace: "nowrap"
					}}
				>
					{label}
				</CaptionText>
				{scope && <DashboardScopeChip scope={scope} />}
			</Box>
			{isLoading ? (
				<>
					<Skeleton height={42} sx={{ mt: 0.5 }} variant="text" width="60%" />
					<Skeleton height={16} sx={{ mt: 0.5 }} variant="text" width="40%" />
				</>
			) : isError ? (
				<Box onClick={(event) => event.stopPropagation()} sx={{ mt: 0.5 }}>
					<RetryableErrorState dense message={errorMessage} onRetry={onRetry} retryLabel={retryLabel} title="" />
				</Box>
			) : (
				<>
					<Typography sx={{ color: "text.primary", fontWeight: 700, letterSpacing: "-0.01em", mt: 0.5 }} variant="h4">
						{value}
					</Typography>
					{hint && (
						<CaptionText sx={{ color: "text.secondary", display: "block", lineHeight: 1.3, mt: 0.5 }}>
							{hint}
						</CaptionText>
					)}
					{footer && (
						<Box onClick={(event) => event.stopPropagation()} sx={{ mt: 1 }}>
							{footer}
						</Box>
					)}
				</>
			)}
		</Box>
		{icon && (
			<Box
				sx={{
					alignItems: "center",
					backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.09),
					borderRadius: 2.5,
					display: "flex",
					flexShrink: 0,
					height: 44,
					justifyContent: "center",
					width: 44
				}}
			>
				<Icon sx={{ color: "primary.main", fontSize: "1.4rem" }}>{icon}</Icon>
			</Box>
		)}
	</Paper>
);

export default DashboardKpiTile;
